// Renderizador de markdown mínimo y seguro (sin dependencias). Cubre lo que la
// UI clínica realmente recibe del backend/LLM: **negrita**, bloques ```code```,
// listas con "- ", encabezados "#"/"##"/"###", divisores "---" y párrafos.
// Reemplaza el `whitespace-pre-wrap` que mostraba los `**` y ```yaml``` literales
// (hallazgo U2).
import { Fragment, type ReactNode } from "react";

function renderInline(text: string): ReactNode[] {
  // **negrita** + `código` + _itálica_. La itálica exige límites de no-palabra
  // para no romper identificadores con guion bajo (FONASA_A, snake_case).
  const out: ReactNode[] = [];
  const re = /(\*\*([^*]+)\*\*|`([^`]+)`|(?<![A-Za-z0-9])_([^_\n]+)_(?![A-Za-z0-9]))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[2] !== undefined) out.push(<strong key={k++}>{m[2]}</strong>);
    else if (m[3] !== undefined)
      out.push(
        <code key={k++} className="rounded bg-[var(--color-surface-2)] px-1 py-0.5 text-[0.92em]">
          {m[3]}
        </code>,
      );
    else if (m[4] !== undefined) out.push(<em key={k++}>{m[4]}</em>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function MarkdownLite({ content, className }: { content: string; className?: string }) {
  const lines = (content ?? "").split("\n");
  const at = (j: number): string => lines[j] ?? "";
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = at(i);

    // Bloque de código ```...```
    if (line.trim().startsWith("```")) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !at(i).trim().startsWith("```")) {
        buf.push(at(i));
        i++;
      }
      i++; // cerrar fence
      blocks.push(
        <pre
          key={key++}
          className="my-2 overflow-x-auto rounded-lg bg-[var(--color-surface-2)] p-3 text-[12.5px] leading-relaxed"
        >
          <code>{buf.join("\n")}</code>
        </pre>,
      );
      continue;
    }

    // Divisor ---
    if (line.trim() === "---") {
      blocks.push(<hr key={key++} className="my-3 border-[var(--color-border)]" />);
      i++;
      continue;
    }

    // Encabezados
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      const lvl = (h[1] ?? "").length;
      blocks.push(
        <p key={key++} className={cnHeading(lvl)}>
          {renderInline(h[2] ?? "")}
        </p>,
      );
      i++;
      continue;
    }

    // Listas con "- " o "• "
    if (/^\s*[-•]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-•]\s+/.test(at(i))) {
        items.push(at(i).replace(/^\s*[-•]\s+/, ""));
        i++;
      }
      blocks.push(
        <ul key={key++} className="my-1.5 list-disc space-y-0.5 pl-5">
          {items.map((it, idx) => (
            <li key={idx}>{renderInline(it)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    // Línea en blanco
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Párrafo (junta líneas consecutivas no especiales)
    const para: string[] = [];
    while (
      i < lines.length &&
      at(i).trim() !== "" &&
      !at(i).trim().startsWith("```") &&
      at(i).trim() !== "---" &&
      !/^(#{1,3})\s+/.test(at(i)) &&
      !/^\s*[-•]\s+/.test(at(i))
    ) {
      para.push(at(i));
      i++;
    }
    blocks.push(
      <p key={key++} className="my-1">
        {para.map((p, idx) => (
          <Fragment key={idx}>
            {idx > 0 && <br />}
            {renderInline(p)}
          </Fragment>
        ))}
      </p>,
    );
  }

  return <div className={className}>{blocks}</div>;
}

function cnHeading(lvl: number): string {
  if (lvl === 1) return "mt-2 mb-1 text-base font-bold";
  if (lvl === 2) return "mt-2 mb-1 text-sm font-bold";
  return "mt-1.5 mb-0.5 text-[13px] font-semibold";
}
